import React, { ReactNode } from 'react'

import NodeType from 'types/transformation/enums/NodeType'

import Box from 'components_new/atoms/Box'
import Handle, { Position } from 'components_new/atoms/Handle'
import Icon, { IconName } from 'components_new/atoms/Icon'

import { BOARD, getAccent } from './accents'

export enum InputType {
  SINGLE = 'SINGLE',
  DOUBLE = 'DOUBLE'
}

/*
 * Vertical positions for the two target handles on double-input nodes
 * (left join, unite), aligned with the centers of the two field rows that
 * open the node body.
 */
const DOUBLE_HANDLE_TOP = { left: 67, right: 102 }

interface NodeProps {
  children: ReactNode
  error?: boolean
  icon: IconName
  inputType: InputType | null // handles
  locked?: boolean
  nodeNumber: number
  selected: boolean
  title: string
  type: NodeType
}

/**
 * Base card for all transformation-board nodes: a white rounded card with a
 * header row (accent-colored icon chip, title, node-number badge) and a
 * type-specific body. Connection handles are colored by the node's accent.
 */
const Node = (props: NodeProps) => {
  const {
    children,
    error,
    icon,
    inputType,
    locked,
    nodeNumber,
    selected,
    title,
    type
  } = props

  const accent = getAccent(type)

  const borderColor = error
    ? 'var(--warning)'
    : selected
      ? accent.stark
      : BOARD.border

  return (
    <>
      <Box
        sx={{
          backgroundColor: 'common.white',
          borderRadius: '14px',
          border: `1px solid ${borderColor}`,
          boxShadow: selected
            ? `0 0 0 3px ${accent.soft}, ${BOARD.shadowLifted}`
            : BOARD.shadowRest,
          transition: 'box-shadow 0.15s, border-color 0.15s',
          '&:hover': {
            boxShadow: selected
              ? `0 0 0 3px ${accent.soft}, ${BOARD.shadowLifted}`
              : `${BOARD.shadowRest}, 0 8px 18px rgba(16, 24, 40, 0.10)`
          }
        }}
      >
        {title && (
          <Box
            sx={{
              alignItems: 'center',
              borderBottom: `1px solid ${BOARD.borderSoft}`,
              display: 'flex',
              gap: 1,
              px: 1.5,
              py: 1
            }}
          >
            <Box
              sx={{
                alignItems: 'center',
                backgroundColor: accent.bg,
                borderRadius: '7px',
                color: accent.stark,
                display: 'flex',
                flex: 'none',
                height: 25,
                justifyContent: 'center',
                width: 25
              }}
            >
              <Icon name={icon} sx={{ fontSize: '15px' }} />
            </Box>
            <Box
              sx={{
                color: BOARD.textPrimary,
                flex: 1,
                fontFamily: BOARD.fontDisplay,
                fontSize: '13.5px',
                fontWeight: 600,
                letterSpacing: '-0.02em',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {title}
            </Box>
            {locked ? (
              <Icon name="Lock" sx={{ color: BOARD.textMuted }} />
            ) : null}
            {nodeNumber ? (
              <Box
                sx={{
                  alignItems: 'center',
                  backgroundColor: BOARD.signalGreen,
                  borderRadius: '20px',
                  color: 'common.white',
                  display: 'flex',
                  flex: 'none',
                  fontFamily: BOARD.fontBody,
                  fontSize: '10.5px',
                  fontWeight: 700,
                  height: 19,
                  justifyContent: 'center',
                  minWidth: 19,
                  px: '5px'
                }}
              >
                {nodeNumber}
              </Box>
            ) : null}
          </Box>
        )}
        <Box
          sx={{
            minHeight: '2rem',
            px: 1.5,
            py: 1.25
          }}
        >
          {children}
        </Box>
      </Box>
      <InputHandles color={accent.stark} type={inputType} />
      {/* Output handle */}
      {type !== NodeType.MAP ? (
        <Handle
          color={accent.stark}
          isConnectable
          type="source"
          position={Position.Right}
        />
      ) : null}
    </>
  )
}

const InputHandles = (props: { color: string; type: InputType | null }) => {
  const { color, type } = props

  if (!type) {
    return null
  }

  if (type === InputType.DOUBLE) {
    return (
      <>
        <Handle
          color={color}
          id="left"
          isConnectable={true}
          type="target"
          position={Position.Left}
          sx={{ top: `${DOUBLE_HANDLE_TOP.left}px` }}
        />
        <Handle
          color={color}
          id="right"
          isConnectable={true}
          type="target"
          position={Position.Left}
          sx={{ top: `${DOUBLE_HANDLE_TOP.right}px` }}
        />
      </>
    )
  }

  return (
    <Handle
      color={color}
      isConnectable={true}
      type="target"
      position={Position.Left}
    />
  )
}

export default Node
